import { forwardRef, useCallback, useEffect, useImperativeHandle, useState } from "react";
import {
	Text,
	useDisclosure,
	Spinner,
} from "@chakra-ui/react";
import { FiEdit2, FiPlus, FiTrash2 } from "react-icons/fi";

import { Button } from "newui/components/Button";
import { cn } from "utils/helpers";
import { Modal } from "newui/components/ui/Modal";
import type {
	ScheduleColumn,
	ScheduleOfRatesData,
	ScheduleRow,
} from "./schedule-of-rates.types";
import {
	getScheduleOfRates,
	saveScheduleOfRates,
	deleteScheduleOfRates,
	initScheduleFromTemplate,
} from "../../api";
import { useScheduleEditor } from "./useScheduleEditor";
import { ScheduleTableView } from "./components/ScheduleOfRatesTableView";
import { EditScheduleModal } from "./components/EditScheduleOfRatesModal";

export interface ScheduleOfRatesHandle {
	validate: () => boolean;
	save: () => Promise<boolean>;
}

export interface ScheduleOfRatesProps {
	disabled: boolean;
	hasValidationError?: boolean;
	inviteId: number;
	isClientView: boolean;
}

const ScheduleOfRates = forwardRef<ScheduleOfRatesHandle, ScheduleOfRatesProps>(({
	disabled,
	hasValidationError = false,
	inviteId,
	isClientView,
}, ref) => {
	const [schedule, setSchedule] = useState<ScheduleOfRatesData | null>(null);
	const [isLoading, setIsLoading] = useState(true);
	const [isInitializing, setIsInitializing] = useState(false);
	const [isSaving, setIsSaving] = useState(false);
	const [isDeleting, setIsDeleting] = useState(false);
	const [showEditor, setShowEditor] = useState(false);
	const [isEditModalOpen, setIsEditModalOpen] = useState(false);
	const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();
	const {
		isOpen: isRowDeleteOpen,
		onOpen: onRowDeleteOpen,
		onClose: onRowDeleteClose,
	} = useDisclosure();
	const [pendingRowDeleteId, setPendingRowDeleteId] = useState<number | null>(null);

	const editor = useScheduleEditor({
		initialColumns: [],
		initialRows: [],
	});

	const applyScheduleData = useCallback((data: ScheduleOfRatesData) => {
		setSchedule(data);
		editor.reset(data.columns, data.rows);
	}, [editor.reset]);

	useImperativeHandle(ref, () => ({
		validate: editor.validate,
		save: async (): Promise<boolean> => {
			if (!editor.validate()) {
				return false;
			}
			const cols = editor.getColumns();
			const rowData = editor.getRows();
			if (cols.length === 0 || rowData.length === 0) {
				return true;
			}
			setIsSaving(true);
			try {
				const data = await saveScheduleOfRates(inviteId, {
					name: "Schedule of Rates",
					columns: cols.map((c) => ({
						name: c.label,
						type: c.columnType,
						sort_order: c.sortOrder,
					})),
					rows: rowData.map((r) => ({
						sort_order: r.sortOrder,
						cells: r.values,
					})),
				});
				applyScheduleData(data);
				setShowEditor(false);
				return true;
			} catch {
				return false;
			} finally {
				setIsSaving(false);
			}
		},
	}), [editor.validate, editor.getColumns, editor.getRows, inviteId, applyScheduleData]);

	const fetchSchedule = useCallback(async () => {
		setIsLoading(true);
		try {
			const data = await getScheduleOfRates(inviteId);
			if (data) {
				applyScheduleData(data);
			}
		} catch {
			// Error handled by global interceptor
		} finally {
			setIsLoading(false);
		}
	}, [inviteId, applyScheduleData]);

	useEffect(() => {
		fetchSchedule();
	}, [fetchSchedule]);

	const handleAddSchedule = useCallback(async () => {
		setIsInitializing(true);
		try {
			const data = await initScheduleFromTemplate(inviteId);
			if (data) {
				applyScheduleData(data);
			} else {
				setShowEditor(true);
				setIsEditModalOpen(true);
			}
		} catch {
			setShowEditor(true);
			setIsEditModalOpen(true);
		} finally {
			setIsInitializing(false);
		}
	}, [inviteId, applyScheduleData]);

	const handleDelete = useCallback(async () => {
		if (!schedule) {
			return;
		}
		setIsDeleting(true);
		try {
			await deleteScheduleOfRates(inviteId, schedule.id);
			setSchedule(null);
			editor.reset([], []);
			setShowEditor(false);
		} catch {
			// Error handled by global interceptor
		} finally {
			setIsDeleting(false);
			onDeleteClose();
		}
	}, [schedule, inviteId, onDeleteClose, editor.reset]);

	const handleColumnsSave = useCallback(
		(newColumns: ScheduleColumn[]) => {
			// Re-map existing row values to the new column structure
			const oldColumns = editor.getColumns();
			const currentRows = editor.getRows();

			// Build a mapping from old column labels to new column ids
			// This preserves data when columns are reordered/renamed
			const oldLabelToId: Record<string, number> = {};
			oldColumns.forEach((col) => {
				oldLabelToId[`${col.label}-${col.columnType}`] = col.id;
			});

			const remappedRows: ScheduleRow[] = currentRows.map((row) => {
				const newValues: Record<number, string> = {};
				newColumns.forEach((newCol) => {
					const oldId = oldLabelToId[`${newCol.label}-${newCol.columnType}`];
					if (oldId !== undefined && row.values[oldId] !== undefined) {
						newValues[newCol.id] = row.values[oldId];
					}
				});
				return { ...row, values: newValues };
			});

			editor.reset(newColumns, remappedRows);
			setIsEditModalOpen(false);
		},
		[editor.getColumns, editor.getRows, editor.reset]
	);

	const confirmRemoveRow = useCallback(
		(rowId: number) => {
			setPendingRowDeleteId(rowId);
			onRowDeleteOpen();
		},
		[onRowDeleteOpen]
	);

	const handleConfirmRowDelete = useCallback(() => {
		if (pendingRowDeleteId !== null) {
			editor.removeRow(pendingRowDeleteId);
		}
		setPendingRowDeleteId(null);
		onRowDeleteClose();
	}, [pendingRowDeleteId, editor.removeRow, onRowDeleteClose]);

	if (isLoading) {
		return (
			<div
				className="flex items-center justify-center py-8"
				data-testid="schedule-loading"
			>
				<Spinner size="md" />
			</div>
		);
	}

	const hasSchedule = schedule !== null;
	const hasContent = editor.columns.length > 0;
	const isEditable = !disabled && !isClientView;

	// No schedule exists and not in editor mode — show Add button (supplier) or nothing (client)
	if (!hasSchedule && !showEditor) {
		if (!isEditable) {
			return null;
		}
		return (
			<div
				className="mt-6 rounded-xl border border-dashed border-[#D0D5DD] bg-white p-6"
				data-testid="schedule-of-rates-add"
			>
				<div className="flex flex-col items-center gap-3">
					<Text className="font-roboto text-sm text-gray-500">
						No schedule of rates attached to this submission.
					</Text>
					<Button
						size="sm"
						btntype="primary"
						onClick={handleAddSchedule}
						disabled={isInitializing}
						data-testid="add-schedule-btn"
					>
						<FiPlus className="mr-1" />
						{isInitializing ? "Loading..." : "Add Schedule of Rates"}
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div
			className={cn(
				"mt-6 rounded-xl border bg-white shadow-sm",
				hasValidationError ? "border-2 border-red-500" : "border-[#EAECF0]"
			)}
			data-testid="schedule-of-rates"
		>
			{/* Header bar */}
			<div className="flex items-center justify-between border-b border-[#EAECF0] px-4 py-3 bg-blue-100">
				<Text className="font-roboto text-lg font-semibold text-gray-900">
					Schedule of Rates
				</Text>
				<div className="flex items-center gap-2">
					{isEditable && (
						<>
							<Button
								size="xs"
								btntype="secondary"
								onClick={() => setIsEditModalOpen(true)}
								data-testid="edit-schedule-btn"
							>
								<FiEdit2 size={14} className="mr-1" />
								Edit Columns
							</Button>
							{(hasSchedule || showEditor) && (
								<Button
									size="xs"
									btntype="plain-error"
									className="rounded p-1 !hover:bg-red-50 !hover:text-red-600 !text-red-400 !bg-white !border-red-400"
									onClick={hasSchedule ? onDeleteOpen : () => {
										setShowEditor(false);
										editor.reset([], []);
									}}
									aria-label="Delete schedule"
									data-testid="delete-schedule-btn"
								>
									<FiTrash2 size={14} /> <span className="pl-2">Remove</span>
								</Button>
							)}
						</>
					)}
				</div>
			</div>

			{/* Table view */}
			{hasContent && (
				<ScheduleTableView
					columns={editor.getColumns()}
					disabled={disabled || isClientView}
					onAddRow={isEditable ? editor.addRow : undefined}
					onCellChange={isEditable ? editor.updateCellValue : undefined}
					onCurrencyBlur={isEditable ? editor.formatCurrencyCell : undefined}
					onRemoveRow={isEditable ? confirmRemoveRow : undefined}
					rows={editor.rows}
					validationErrors={editor.validationErrors}
				/>
			)}

			{/* Add Row for empty schedule with columns */}
			{!hasContent && isEditable && (
				<div className="flex items-center justify-center py-6">
					<Text className="text-sm text-gray-400">
						Use &ldquo;Edit Columns&rdquo; to configure the schedule structure.
					</Text>
				</div>
			)}

			{/* Edit columns modal */}
			<EditScheduleModal
				initialColumns={editor.getColumns()}
				isOpen={isEditModalOpen}
				onClose={() => setIsEditModalOpen(false)}
				onSave={handleColumnsSave}
			/>

			{/* Delete schedule confirmation */}
			<Modal
				isOpen={isDeleteOpen}
				onClose={onDeleteClose}
				title="Remove Schedule of Rates"
				closeOnEsc
				closeOnOverlayClick
				primaryButton={{
					text: isDeleting ? "Deleting..." : "Delete",
					onClick: handleDelete,
				}}
				secondaryButton={{
					onClick: onDeleteClose,
					text: "Cancel",
				}}
				iconType="error"
			>
				<Text>
					This will permanently delete your schedule of rates from this RFx Submission.
				</Text>
				<Text>
					This action cannot be undone.
				</Text>
			</Modal>

			{/* Delete row confirmation */}
			<Modal
				isOpen={isRowDeleteOpen}
				onClose={onRowDeleteClose}
				title="Remove Row"
				closeOnEsc
				closeOnOverlayClick
				primaryButton={{
					text: "Delete",
					onClick: handleConfirmRowDelete,
				}}
				secondaryButton={{
					onClick: onRowDeleteClose,
					text: "Cancel",
				}}
				iconType="error"
			>
				<Text>
					Are you sure you want to remove this row?
				</Text>
				<Text>
					This action cannot be undone.
				</Text>
			</Modal>
		</div>
	);
});

ScheduleOfRates.displayName = "ScheduleOfRates";

export { ScheduleOfRates };
